import { FC, useState, useEffect } from 'react';
import useReactRouter from 'use-react-router';
import Button from 'jbc-front/components/Button';

import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { yupResolver } from '@hookform/resolvers/yup';
import { salaryEarnings as calcSalaryEarnings, calcTotalSalaryIncome } from './utils';
import { InputWithText } from '@jbc-year-end-adj/2024/components/form/InputWithText';
import { Section } from '@jbc-year-end-adj/2024/components/ui/Section';
import { Row } from '@jbc-year-end-adj/2024/components/form/Row';
import { amountFormat } from '@jbc-year-end-adj/2024/utils/formatter';
import { FormItem } from '@jbc-year-end-adj/2024/components/form/FormItem';
import { useEmployeeMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { AmountField } from '@jbc-year-end-adj/2024/components/react-hook-form/AmountField';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { generateDefaultValues, schema, Schema, Income } from './schema';
import { ButtonContainer as Buttons } from '@jbc-year-end-adj/2024/features/questions/components/ButtonContainer';
import { Profile } from './query';
import { ANSWER_SALARY_INCOME } from './mutation';

type FormProps = {
  profile: Profile;
};

export const Form: FC<FormProps> = ({ profile }) => {
  const [answer] = useEmployeeMutation(ANSWER_SALARY_INCOME);
  const { history } = useReactRouter();
  const income = profile?.income;
  const [totalSalaryIncome, setTotalSalaryIncome] = useState(0);
  const [salaryEarnings, setSalaryEarnings] = useState(0);

  const formerJobsPaymentAmount = profile?.formerJobs.reduce((totalIncome, formerJob) => {
    if (formerJob.paymentAmount) {
      return totalIncome + Number(formerJob.paymentAmount);
    } else {
      return totalIncome;
    }
  }, 0);

  const methods = useForm({
    defaultValues: generateDefaultValues(income),
    resolver: yupResolver(schema)
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = methods;

  const mainJobIncome = watch('income.mainJobIncome');
  const sideJobIncome = watch('income.sideJobIncome');

  useEffect(() => {
    const totalSalaryIncome = calcTotalSalaryIncome({
      mainJobIncome: mainJobIncome,
      sideJobIncome: sideJobIncome,
      formerJobsPaymentAmountSum: String(formerJobsPaymentAmount)
    });
    setTotalSalaryIncome(totalSalaryIncome);
    setSalaryEarnings(calcSalaryEarnings(totalSalaryIncome));
  }, [mainJobIncome, sideJobIncome, formerJobsPaymentAmount]);

  const onSubmit: SubmitHandler<Schema> = async (data: { income: Income }) => {
    await answer({ variables: { income: data.income } });
    history.push('/employee_input');
  };

  return (
    <FormProvider {...methods}>
      <Section>
        <Section.Header>本人の給与収入</Section.Header>
        <Section.Body>
          <FormSection>
            <Row>
              <AmountField
                label="給与所得 収入金額（当社）"
                note="前Stepで入力した前職情報は含みません"
                error={errors.income?.mainJobIncome?.message}
                isError={!!errors.income?.mainJobIncome?.message}
                {...register('income.mainJobIncome')}
              />
              <AmountField
                label="給与所得 収入金額（他社）"
                note="前Stepで入力した前職情報は含みません"
                error={errors.income?.sideJobIncome?.message}
                isError={!!errors.income?.sideJobIncome?.message}
                {...register('income.sideJobIncome')}
              />
            </Row>
            <Row>
              <InputWithText label="給与所得 収入金額（前職）" text="円" value={amountFormat(String(formerJobsPaymentAmount))} disabled />
              <FormItem />
            </Row>
            <Row>
              <InputWithText label="給与所得 収入金額（合計）" text="円" value={amountFormat(String(totalSalaryIncome))} disabled />
              <InputWithText label="給与所得 所得金額" text="円" value={amountFormat(String(salaryEarnings))} disabled />
            </Row>
          </FormSection>
        </Section.Body>
      </Section>

      <Buttons>
        <Button primary onClick={handleSubmit(onSubmit)}>
          次へ
        </Button>
      </Buttons>
    </FormProvider>
  );
};
